import { useState, type SyntheticEvent } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Illustration, Link, Message, Spacer, Typography } from "@aviary";
import { BackButton } from "@aviary/layouts";
import { useSearchParams, useNavigate } from "@shared/react-router-dom/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { UserSendMagicLinkUnauthenticatedSharedMutation as UserSendMagicLinkMutationData } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { useUserSendMagicLinkUnauthenticatedSharedMutation as useUserSendMagicLinkMutation } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneySuccessContent.styles";

// URL parameter constants
const URL_PARAMS = {
  STORE_ID: "store_id",
  REDIRECT_PATH: "redirect_path",
  INTAKE: "intake",
} as const;

const BOOLEAN_VALUES = {
  TRUE: "true",
} as const;

// Route path constants
const ROUTE_PATHS = {
  AUTH_CHECKOUT_PREFIX: "/r/checkout/",
  UNAUTH_CHECKOUT_PREFIX: "/checkout/",
  JOURNEY_PATH: "/journey/",
} as const;

type MagicLinkPage = "email" | "success";

interface JourneySuccessContentProps {
  email: string;
  setPage: (page: MagicLinkPage) => void;
  isSwiftFillSignIn?: boolean;
  onBackToLogin?: () => void;
}

const JourneySuccessContent = ({
  email,
  setPage,
  isSwiftFillSignIn,
  onBackToLogin,
}: JourneySuccessContentProps) => {
  const { t } = useTranslation();
  const [showResendSuccess, setShowResendSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams?.get(URL_PARAMS.STORE_ID);
  const redirectPath = searchParams?.get(URL_PARAMS.REDIRECT_PATH);
  const intake = searchParams?.get(URL_PARAMS.INTAKE) === BOOLEAN_VALUES.TRUE;

  const onCompleted = (data: UserSendMagicLinkMutationData) => {
    const { message: errorMessage } = data?.auth?.userSendMagicLink?.errors ?? {};

    if (!errorMessage) {
      setShowResendSuccess(true);
    }
  };

  const [sendMagicLink] = useUserSendMagicLinkMutation();
  const handleSendLink = (e: SyntheticEvent) => {
    e.preventDefault();

    gRecaptchaExecute(gToken => {
      sendMagicLink({
        variables: {
          input: {
            captchaToken: gToken,
            email,
            intake,
            ...(storeId && { storeId }),
            ...(redirectPath && { redirectPath }),
          },
        },
        onCompleted,
      });
    });
  };

  const backButton = () => {
    if (isSwiftFillSignIn) {
      return <BackButton onClick={() => navigate(-1)} text={t(l.auth.back)} isUnderLined={false} />;
    }

    return (
      <BackButton onClick={() => setPage("email")} text={t(l.auth.back)} isUnderLined={false} />
    );
  };

  const handleDifferentSignInOption = (e: SyntheticEvent) => {
    e.preventDefault();

    if (isSwiftFillSignIn) return navigate(-1);

    // If onBackToLogin is provided (journey flow), use it to go back to previous view
    if (onBackToLogin) {
      onBackToLogin();

      return;
    }

    // Journey checkout redirect logic - convert authenticated to unauthenticated path
    if (
      redirectPath &&
      redirectPath.includes(ROUTE_PATHS.AUTH_CHECKOUT_PREFIX) &&
      redirectPath.includes(ROUTE_PATHS.JOURNEY_PATH)
    ) {
      // Convert from authenticated path /r/checkout/... to unauthenticated path /checkout/...
      // This preserves the journey path segments (e.g. email, nextStep) in the URL
      const unauthenticatedPath = redirectPath.replace(
        ROUTE_PATHS.AUTH_CHECKOUT_PREFIX,
        ROUTE_PATHS.UNAUTH_CHECKOUT_PREFIX
      );

      // Use browser navigation instead of React Router since it doesn't work in unauthenticated SPA
      window.location.href = unauthenticatedPath;

      return;
    }

    return navigate(authRoutes.login);
  };

  return (
    <>
      {!!showResendSuccess && (
        <Message isColor="success">
          {t(l.signIn.continueWithoutPassword.successPage.resentSuccess)}
        </Message>
      )}

      <div css={styles.textWrapper}>
        <Typography type="h2">{t(l.signIn.continueWithoutPassword.successPage.title)}</Typography>
        <Typography type="h3">
          {t(l.signIn.continueWithoutPassword.successPage.subTitle)}
        </Typography>
        <Spacer height="oneHalf" />
        <Illustration illustration="envelope" heightAndWidth={12} />
        <Spacer height="oneHalf" />
        <Typography type="body">
          <Trans
            i18nKey={l.signIn.continueWithoutPassword.successPage.emailSent}
            values={{ email }}
            components={{
              emailText: <strong />,
            }}
          />
        </Typography>
        <Spacer height="one" />
        <Typography>
          <Trans
            i18nKey={l.signIn.continueWithoutPassword.successPage.DidNotGetEmail}
            components={{
              resendLink: <Link onClick={handleSendLink} />,
              signInLink: <Link onClick={handleDifferentSignInOption} />,
            }}
          />
        </Typography>
      </div>

      {backButton()}
    </>
  );
};

export { JourneySuccessContent };
